import { useRef, useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../api';
import { UploadSimple, Trash, ImageSquare, Spinner, Crop } from '@phosphor-icons/react';
import ImageCropDialog from './ImageCropDialog';

/**
 * BrandingFileUploader
 *
 * Small drag-and-drop / file picker for a single branding asset (logo, favicon, background).
 * For "logo" it opens a crop editor before upload.
 *
 * Props:
 *  - establishmentId, kind ('logo'|'favicon'|'background'), label, accept, maxKB, currentUrl, onChange, hint
 */
export default function BrandingFileUploader({
  establishmentId,
  kind,
  label,
  accept,
  maxKB,
  currentUrl,
  onChange,
  hint,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [isDrag, setIsDrag] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropFile, setCropFile] = useState(null);

  const pickFile = () => inputRef.current?.click();

  const enableCrop = kind === 'logo' || kind === 'background';
  const cropAspect = kind === 'background' ? 'landscape' : 'square';
  const cropTitle = kind === 'background' ? 'Ajustar imagen de fondo' : 'Ajustar logo';

  const doUpload = async (file) => {
    if (!file) return;
    if (maxKB && file.size > maxKB * 1024) {
      toast.error(`Archivo demasiado grande. Máximo permitido: ${maxKB} KB`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await api.post(
        `/establishments/${establishmentId}/branding/upload?kind=${kind}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const url = resp.data?.url;
      if (url) {
        onChange?.(url);
        toast.success(`${label} actualizado`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const startUpload = (file) => {
    if (!file) return;
    // For SVGs or when crop is not enabled, upload directly
    if (!enableCrop || file.type === 'image/svg+xml') {
      doUpload(file);
      return;
    }
    setCropFile(file);
    setCropOpen(true);
  };

  const clear = async () => {
    setUploading(true);
    try {
      await api.delete(`/establishments/${establishmentId}/branding/${kind}`);
      onChange?.(null);
      toast.success('Archivo removido');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al remover');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDrag(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) startUpload(file);
  };

  return (
    <div className="space-y-1.5" data-testid={`branding-uploader-${kind}`}>
      <div className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
        {label}
        {enableCrop && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400" title="Se abre editor de recorte antes de subir">
            <Crop size={10} />
            recorte
          </span>
        )}
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDrag(true); }}
        onDragLeave={() => setIsDrag(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed rounded-lg p-3 flex items-center gap-3 transition ${
          isDrag ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 bg-slate-50'
        }`}
      >
        {/* Preview */}
        <div className={`h-16 w-16 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 bg-white border border-slate-100 ${
          kind === 'background' ? 'w-24' : ''
        }`}>
          {uploading ? (
            <Spinner size={22} className="animate-spin text-slate-400" />
          ) : currentUrl ? (
            <img
              src={currentUrl}
              alt={label}
              className="h-full w-full object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              data-testid={`preview-${kind}`}
            />
          ) : (
            <ImageSquare size={22} className="text-slate-300" />
          )}
        </div>

        {/* Actions */}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-slate-500 truncate mb-1.5" title={currentUrl || 'Sin archivo'}>
            {currentUrl ? 'Cargado — arrastra para reemplazar' : (hint || 'Arrastra o selecciona un archivo')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={pickFile}
              disabled={uploading}
              data-testid={`upload-${kind}-btn`}
            >
              <UploadSimple size={13} className="mr-1" />
              {currentUrl ? 'Reemplazar' : 'Subir'}
            </Button>
            {currentUrl && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-xs text-rose-600 hover:text-rose-700 hover:border-rose-300"
                onClick={clear}
                disabled={uploading}
                data-testid={`remove-${kind}-btn`}
              >
                <Trash size={13} className="mr-1" />
                Quitar
              </Button>
            )}
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => startUpload(e.target.files?.[0])}
          data-testid={`file-input-${kind}`}
        />
      </div>

      {/* Crop editor for logo / background */}
      {enableCrop && (
        <ImageCropDialog
          open={cropOpen}
          onOpenChange={setCropOpen}
          file={cropFile}
          aspect={cropAspect}
          title={cropTitle}
          outputSize={kind === 'background' ? 800 : 512}
          onConfirm={(f) => doUpload(f)}
        />
      )}
    </div>
  );
}

